import type {
  ApplicationDto,
  FundsAdditionalIncomeDto as BaseFundsAdditionalIncomeDto,
  FundsSalariedDto as BaseFundsSalariedDto,
  FundsSelfEmployedDto as BaseFundsSelfEmployedDto,
} from '@/api/backend/users/applications';
import { mapFromApi } from '@/mappers';
import { periodic } from '@/mappers/helpers';
import type { ApplicantIncome, SalaryEmployment, SalaryTypeEmum, SelfEmployment, Unemployment } from '@/types';
import type { FrequencyType } from '@/types/api';

export type FundsSalariedDto = BaseFundsSalariedDto & {
  bonusesAmount?: number;
  commissionsAmount?: number;
  overtimeAmount?: number;
  salaryAmount?: number;
  salaryPackagedAmount?: number;
};

const mapSalary = (data: FundsSalariedDto): SalaryEmployment => {
  return {
    id: data.id ?? '',
    type: data.jobType as SalaryTypeEmum,
    salary: periodic(data.salaryAmountApplicationValue, data.salaryFrequency),
    commission: periodic(data.commissionsAmountApplicationValue, data.commissionsFrequency),
    bonuses: periodic(data.bonusesAmountApplicationValue, data.bonusesFrequency),
    overtime: periodic(data.overtimeAmountApplicationValue, data.overtimeFrequency),
    salarySacrifice: data.isSalarySacrifice ?? false,
    employedSince: data.employedSince ?? '',
    employerName: data.nameOfEmployer ?? '',
    employerAddress: {
      addressLine: data.employerLine1 ?? '',
      addressLine2: data.employerLine2 ?? '',
      locality: data.employerCity ?? '',
      state: data.employerState?.toUpperCase() ?? '',
      postcode: data.employerPostCode ?? '',
      countryCode: 'au',
    },
  };
};

export type FundsSelfEmployedDto = BaseFundsSelfEmployedDto & {
  salaryAmount?: number;
  salaryAmountShaded?: number;
  providedDocument?: string;
  otherProvidedDocument?: string;
};

const mapSelfEmployment = (data: FundsSelfEmployedDto): SelfEmployment => {
  return {
    id: data.id ?? '',
    type: 'self_employed',
    salary: periodic(data.salaryAmountApplicationValue, data.salaryFrequency),
    businessAddress: mapFromApi.address(data),
    businessType: data.businessType ?? '',
    tradingName: data.tradingName ?? '',
    occupation: data.occupation ?? '',
    industry: data.industry ?? '',
    gstRegistered: data.isGSTRegistered ?? false,
    abn: data.australianBusinessNumber ?? '',
    soleTrader: data.isSoleTrader ?? false,
    hasTaxReturns: data.hasTaxReturnsLastTwoFinancialYears ?? false,
    employedSince: data.employedSince ?? '',
    providedDocument: data.providedDocument ?? '',
  };
};

//  Adding frequency types to the additional income dto
export type FundsAdditionalIncomeDto = BaseFundsAdditionalIncomeDto & {
  centerlinkPayments?: number;
  centerlinkPaymentsShaded?: number;
  centerlinkPaymentsFrequency?: FrequencyType;
  childSupportMaintenance?: number;
  childSupportMaintenanceShaded?: number;
  childSupportMaintenanceFrequency?: FrequencyType;
  dividends?: number;
  dividendsShaded?: number;
  dividendsFrequency?: FrequencyType;
  familyAllowance?: number;
  familyAllowanceShaded?: number;
  familyAllowanceFrequency?: FrequencyType;
  familyTaxBenefits?: number;
  familyTaxBenefitsShaded?: number;
  familyTaxBenefitsFrequency?: FrequencyType;
  governmentPension?: number;
  governmentPensionShaded?: number;
  governmentPensionFrequency?: FrequencyType;
  other?: number;
  otherShaded?: number;
  otherFrequency?: FrequencyType;
  superannuation?: number;
  superannuationShaded?: number;
  superannuationFrequency?: FrequencyType;
};

const mapAdditionalIncome = (data?: FundsAdditionalIncomeDto[]) => {
  const funds = data ?? [];

  const centrelinkPayments = funds.find((item) => item.incomeType === 'centrelink_payments');
  const childSupportMaintenance = funds.find((item) => item.incomeType === 'child_support_maintenance');
  const familyAllowance = funds.find((item) => item.incomeType === 'family_allowance');
  const familyTaxBenefit = funds.find((item) => item.incomeType === 'family_tax_benefits');
  const governmentPension = funds.find((item) => item.incomeType === 'government_pension');
  const dividends = funds.find((item) => item.incomeType === 'dividends');
  const superannuation = funds.find((item) => item.incomeType === 'superannuation');
  const other = funds.find((item) => item.incomeType === 'other');

  return {
    id: '',
    centrelinkPayments: periodic(centrelinkPayments?.amount ?? 0, centrelinkPayments?.frequency ?? 'monthly'),
    childSupportMaintenance: periodic(
      childSupportMaintenance?.amount ?? 0,
      childSupportMaintenance?.frequency ?? 'monthly',
    ),
    familyAllowance: periodic(familyAllowance?.amount ?? 0, familyAllowance?.frequency ?? 'monthly'),
    familyTaxBenefit: periodic(familyTaxBenefit?.amount ?? 0, familyTaxBenefit?.frequency ?? 'monthly'),
    governmentPension: periodic(governmentPension?.amount ?? 0, governmentPension?.frequency ?? 'monthly'),
    dividendsInvestments: periodic(dividends?.amount ?? 0, dividends?.frequency ?? 'monthly'),
    superannuation: periodic(superannuation?.amount ?? 0, superannuation?.frequency ?? 'monthly'),
    other: periodic(other?.amount ?? 0, other?.frequency ?? 'monthly'),
  };
};

export const income = (applicantId: string, application: ApplicationDto): ApplicantIncome | null => {
  const sal: SalaryEmployment[] = (application.fundsSalaried ?? [])
    .filter((s: FundsSalariedDto) => s.applicantId === applicantId)
    .map(mapSalary);

  const sal2: SelfEmployment[] = (application.fundsSelfEmployed ?? [])
    .filter((s: FundsSelfEmployedDto) => s.applicantId === applicantId)
    .map(mapSelfEmployment);

  const unemployed: Unemployment[] = [];
  (application.applicants ?? [])
    .filter((applicant) => applicant.id === applicantId)
    .forEach((applicant) => {
      if (applicant.isRetired) {
        unemployed.push({ id: `${applicant.id}-retired`, type: 'retired' });
      }
      if (applicant.isUnemployed) {
        unemployed.push({ id: `${applicant.id}-unemployed`, type: 'unemployed' });
      }
    });

  const details = (application.employmentDetails ?? []).find(
    (employmentDetail) => employmentDetail.applicantId === applicantId,
  );
  const additional = (application.fundsAdditionalIncome ?? []).filter(
    (additionalIncome) => additionalIncome.applicantId === applicantId,
  );
  const expect = (application.incomeExpectations ?? []).find((e) => e.applicantId === applicantId);
  const employment = [...sal, ...sal2, ...unemployed];
  return employment.length === 0
    ? null
    : {
        incomeExpectation:
          application.incomeExpectations.find((expectation) => expectation.applicantId === applicantId) || {},
        employmentDetail: application.employmentDetails.find((detail) => detail.applicantId === applicantId) || {},
        applicantId,
        employment: [...sal, ...sal2, ...unemployed],
        ...mapAdditionalIncome(additional),
        shortTermChange: details?.employmentChangeInShortTerm ?? false,
        shortTermEmploymentChange: details?.detailsOnChange ?? '',
        expectIncomeChange: expect?.expectedChangesInNext12Months ?? false,
        effectOnIncomeChange: expect?.financialPosition ?? '',
      };
};
